<template>
  <div class="fullscreen-comparison">
    <div class="fs-header d-flex justify-content-between">
      <div class="d-flex align-items-center">
        <b-breadcrumb class="mr-2" style="background-color: #f0f0f0;">
          <b-breadcrumb-item to="/llm-comparison">LLM Comparison</b-breadcrumb-item>
          <b-breadcrumb-item active>Comparison Viewer</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
      <div>
        <b-button variant="primary"
        size="sm" v-b-tooltip.hover :title="filename" as="a" :href="pdfUrl" target="_blank">
          <i class="fas fa-file-pdf"></i> 
          View Source Document
          <i class="fas fa-external-link-alt ml-1"></i>
      </b-button>
      </div>
    </div>

    <div class="fs-body">
      <div class="d-flex align-items-center mb-2">
      </div>
      <b-tabs v-model="activeTab">
        <b-tab :title="leftModel.name || 'Model A'"></b-tab>
        <b-tab :title="rightModel.name || 'Model B'"></b-tab>
      </b-tabs>
      <div class="d-flex model-panels">
        <div class="recall-details-panel p-3">
          <h6 class="panel-title">recall-details</h6>
          <div class="panel-body">
            <div v-if="activeContentExists">
              <field-renderer v-for="(v, k) in activeContent" :key="'active-'+k" :label="k" :value="v" :path="k" side="active" />
            </div>
            <pre v-else class="small">{{ activeRaw }}</pre>
          </div>
        </div>
        <div class="scoring-panel p-3">
          <scoring-panel
            v-if="jobId && activeModelName"
            :job-id="jobId"
            :model="activeModelName"
            :s3-key="s3Key"
            :key="activeModelName"
          />
          <div v-else class="text-muted">
            Loading scoring panel...
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import service from '../../services/llm-comparison'
import ScoringPanel from './ScoringPanel.vue'

// Reuse same FieldRenderer implementation as ComparisonViewer
const FieldRenderer = {
  name: 'FieldRenderer',
  props: { label: { type: [String, Number], required: true }, value: { required: true }, path: { type: String, required: true }, side: { type: String, required: true } },
  data() { return { open: true, expandedProducts: {} } },
  computed: {
    isArray() { return Array.isArray(this.value) },
    isObject() { return this.value && typeof this.value === 'object' && !Array.isArray(this.value) },
    isPrimitive() { return !this.isArray && !this.isObject }
  },
  methods: {
    toggle() { this.open = !this.open },
    toggleProduct(idx) { this.$set(this.expandedProducts, idx, !this.expandedProducts[idx]) },
    stringify(v) { try { return typeof v === 'string' ? v : JSON.stringify(v, null, 2) } catch (e) { return String(v) } },
    formatLabel(label) { return String(label).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }
  },
  render(h) {
    const header = h('div', { class: 'd-flex justify-content-between align-items-center' }, [
      h('div', [ h('strong', this.formatLabel(this.label)) ]),
      this.isObject || this.isArray ? h('div', [ h('b-button', { props: { variant: 'link', size: 'sm' }, on: { click: this.toggle } }, [ this.open ? 'Collapse' : 'Expand' ]) ]) : null
    ])

    let body = null
    if (this.isPrimitive) {
      body = h('div', { class: 'mt-1' }, this.stringify(this.value))
    } else if (this.isObject) {
      const children = Object.keys(this.value || {}).map(k => h('field-renderer', { props: { label: k, value: this.value[k], path: this.path + '.' + k, side: this.side } }))
      body = h('b-collapse', { props: { visible: this.open } }, [ h('div', { class: 'mt-2' }, children) ])
    } else if (this.isArray) {
      if (this.label === 'product_list') {
        // Special grid layout for product_list
        const gridItems = this.value.map((product, idx) => {
          const name = `${idx}. ${product.name}` || 'Unknown Product'
          const catalog = product.catalog_number || 'N/A'
          const udi = product.udi || 'N/A'
          const isExpanded = this.expandedProducts[idx]
          const details = isExpanded ? Object.keys(product).filter(k => k !== 'name' && k !== 'catalog_number' && k !== 'udi').map(k => h('div', { class: '' }, `${this.formatLabel(k)}: ${product[k] || 'N/A'}`)) : []
          return h('div', { class: 'product-card mb-2 p-2 border rounded', key: idx }, [
            h('div', { class: 'font-weight-bold' }, name),
            h('div', { class: '' }, `Catalog: ${catalog}`),
            h('div', { class: '' }, `UDI: ${udi}`),
            h('b-button', { props: { variant: 'link', size: 'sm' }, on: { click: () => this.toggleProduct(idx) } }, isExpanded ? 'Hide Details' : 'Show Details'),
            isExpanded ? h('div', { class: 'mt-2' }, details) : null
          ])
        })
        body = h('div', { class: 'product-grid' }, gridItems)
      } else {
        const items = this.value.map((it, idx) => {
          if (it === null || typeof it === 'undefined' || typeof it === 'string' || typeof it === 'number' || typeof it === 'boolean') {
            return h('div', { class: 'mb-1' }, String(it))
          }
          return h('field-renderer', { props: { label: idx, value: it, path: this.path + '.' + idx, side: this.side } })
        })
        body = h('b-collapse', { props: { visible: this.open } }, [ h('div', { class: 'mt-2' }, items) ])
      }
    }

    return h('div', { class: 'field-group mb-2' }, [ header, body ])
  }
}

FieldRenderer.components = { 'field-renderer': FieldRenderer }

export default {
  name: 'FullScreenComparison',
  components: { 'field-renderer': FieldRenderer, 'scoring-panel': ScoringPanel },
  data() { return { leftModel: {}, rightModel: {}, leftRaw: '', rightRaw: '', activeTab: 0, jobData: null } },
  computed: {
    jobId() {
      return this.jobData && this.jobData._id ? this.jobData._id : (this.$route && this.$route.params ? this.$route.params.id : null)
    },
    activeModelName() {
      return this.activeModel && this.activeModel.name ? this.activeModel.name : null
    },
    s3Key() {
      return this.jobData && this.jobData.sourceFile ? this.jobData.sourceFile.s3Key : ''
    },
    leftContent() {
      if (this.leftModel && this.leftModel.output && typeof this.leftModel.output === 'object') return this.leftModel.output
      if (this.leftModel && typeof this.leftModel === 'object') return this.leftModel
      return null
    },
    rightContent() {
      if (this.rightModel && this.rightModel.output && typeof this.rightModel.output === 'object') return this.rightModel.output
      if (this.rightModel && typeof this.rightModel === 'object') return this.rightModel
      return null
    },
    leftContentExists() { return !!this.leftContent && Object.keys(this.leftContent).length > 0 },
    rightContentExists() { return !!this.rightContent && Object.keys(this.rightContent).length > 0 },
    activeModel() { return this.activeTab === 0 ? this.leftModel : this.rightModel },
    activeContent() {
      if (this.activeModel && this.activeModel.output && typeof this.activeModel.output === 'object') return this.activeModel.output
      if (this.activeModel && typeof this.activeModel === 'object') return this.activeModel
      return null
    },
    activeContentExists() { return !!this.activeContent && Object.keys(this.activeContent).length > 0 },
    activeRaw() { return this.activeTab === 0 ? this.leftRaw : this.rightRaw },
    filename() { return this.jobData && this.jobData.sourceFile ? this.jobData.sourceFile.filename : '' },
    pdfUrl() { return this.jobData && this.jobData.sourceFile ? this.jobData.sourceFile.presignedUrl : '' }
  },
  methods: {
    stringify(v) { try { return typeof v === 'string' ? v : JSON.stringify(v, null, 2) } catch (e) { return String(v) } },
    formatLabel(label) { return String(label).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) },
    async load() {
      const id = this.$route && this.$route.params ? this.$route.params.id : null
      if (!id) return
      try {
        const resp = await service.getComparison(id)
        const job = resp && resp.data ? resp.data : null
        if (!job) return
        this.jobData = job
        const { modelA, modelB, results } = job.models
        const resultA = results ? results.find(r => r.model === modelA) : null
        const resultB = results ? results.find(r => r.model === modelB) : null
        this.leftModel = resultA ? { name: modelA, output: resultA.output } : { name: modelA }
        this.rightModel = resultB ? { name: modelB, output: resultB.output } : { name: modelB }
        this.leftRaw = resultA && resultA.output ? this.stringify(resultA.output) : ''
        this.rightRaw = resultB && resultB.output ? this.stringify(resultB.output) : ''
      } catch (e) {
        // swallow
      }
    },
    close() { this.$router.back() }
  },
  watch: {
    '$route.params.id': 'load'
  },
  mounted() { this.load() }
}
</script>

<style>
/* Constrain overlay to the #core-view container so it doesn't exceed that div */
#core-view { position: relative; }
#core-view .fullscreen-comparison {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
}
.fs-header { padding: 0.75rem 1rem; border-bottom: 1px solid #eee }
.fs-body { flex: 1 1 auto; overflow: hidden }
.model-panels { flex: 1; display: flex; height: 100%; }
.recall-details-panel { flex: 0 0 70%; border-right: 1px solid #eee; display: flex; flex-direction: column; overflow: hidden; }
.scoring-panel { flex: 0 0 30%; display: flex; flex-direction: column; overflow: hidden; }
.panel-title { font-weight: bold; text-transform: lowercase; margin-bottom: 0.5rem; }
.panel-body { flex: 1; overflow: auto; }
.field-group { padding: 0.25rem }
.product-grid { display: grid; grid-template-columns: repeat(auto-fill,1fr); gap: 0.5rem; }
</style>
